import React from 'react';
import { Route, Routes } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Landing from '../pages/Landing';
import RequireAuth from '../components/auth/RequireAuth';
import Dashboard from '../pages/Dashboard';
import { TaxReport } from '../pages/tax/TaxReport';
import TaxConfirmAndSign from '../pages/tax/TaxConfirmAndSign';
import CreateAccount from '../pages/accounts/CreateAccount';
import TaxSelectProperties from '../pages/tax/TaxSelectProperties';
import EmailCheck from '../pages/accounts/EmailCheck';
import ApplyRequirements from '../pages/apply/ApplyRequirements';
import ApplyProperty from '../pages/apply/ApplyProperty';
import ApplyRegistrant from '../pages/apply/ApplyRegistrant';
import ApplyContacts from '../pages/apply/ApplyContacts';
import ApplyPropertyDetails from '../pages/apply/ApplyPropertyDetails';
import ApplyListings from '../pages/apply/ApplyListings';
import ApplyDocuments from '../pages/apply/ApplyDocuments';
import ApplyConfirmation from '../pages/apply/ApplyConfirmation';
import ApplyPayment from '../pages/apply/ApplyPayment';
import ApplySuccess from '../pages/apply/ApplySuccess';
import TaxPayment from '../pages/tax/TaxPayment';
import TaxSuccess from '../pages/tax/TaxSuccess';
import SinglePermit from '../pages/SinglePermit';
import AllPermits from '../pages/AllPermits';
import { useSelector } from 'react-redux';
import { selectConfigExpressRegistrationPlace } from '../store/slices/config/configSelectors';
import UpdateAccount from '../pages/accounts/UpdateAccount';
import UpdateRegistrant from '../pages/update/UpdateRegistrant';
import UpdateContacts from '../pages/update/UpdateContacts';
import UpdatePropertyDetails from '../pages/update/UpdatePropertyDetails';
import UpdateDocuments from '../pages/update/UpdateDocuments';
import UpdateConfirmation from '../pages/update/UpdateConfirmation';
import UpdateSuccess from '../pages/update/UpdateSuccess';
import RenewRequirements from '../pages/renew/RenewRequirements';
import RenewRegistrant from '../pages/renew/RenewRegistrant';
import RenewContacts from '../pages/renew/RenewContacts';
import RenewPropertyDetails from '../pages/renew/RenewPropertyDetails';
import RenewDocuments from '../pages/renew/RenewDocuments';
import RenewConfirmation from '../pages/renew/RenewConfirmation';
import RenewSuccess from '../pages/renew/RenewSuccess';
import RenewPayment from '../pages/renew/RenewPayment';
import PaymentRedirect from '../pages/payment/PaymentRedirect';
import PaymentError from '../pages/payment/PaymentError';
import PaymentCompleted from '../pages/payment/PaymentCompleted';
import PaymentSuccess from '../pages/payment/PaymentSuccess';
import BulkRenewSelectProperties from '../pages/bulk-renewal/BulkRenewSelectProperties';
import BulkRenewRequirements from '../pages/bulk-renewal/BulkRenewRequirements';
import BulkRenewConfirmation from '../pages/bulk-renewal/BulkRenewConfirmation';
import ErrorPage from '../pages/ErrorPage';
import ErrorBoundaryWrapper from '../components/error/ErrorBoundaryWrapper';
import AdminUpdateProperty from '../pages/admin-update/AdminUpdateProperty';
import AdminRequireAuth from '../components/auth/AdminRequireAuth';
import AdminUpdateRegistrant from '../pages/admin-update/AdminUpdateRegistrant';
import AdminUpdateContacts from '../pages/admin-update/AdminUpdateContacts';
import AdminUpdateOwner from '../pages/admin-update/AdminUpdateOwner';
import AdminUpdatePropertyDetails from '../pages/admin-update/AdminUpdatePropertyDetails';
import AdminUpdateSuccess from '../pages/admin-update/AdminUpdateSuccess';
import AdminUpdateDocuments from '../pages/admin-update/AdminUpdateDocuments';
import AdminUpdateConfirmation from '../pages/admin-update/AdminUpdateConfirmation';
import AdminUpdatePortalAccount from '../pages/admin-update/AdminUpdatePortalAccount';
import CyberSource from '../pages/payment/custom/Cybersource';
import { CardConnect } from 'src/pages/payment/custom/CardConnect';
import {
  TaxCertConfirmation,
  TaxCertContacts,
  TaxCertProperty,
  TaxCertRegistrant,
  TaxCertRequirements,
  TaxCertSuccess,
} from '../pages/tax-certificate';
import { PaymentCancel } from 'src/pages/payment/PaymentCancel';

const AppRoutes = () => {
  const cityIdentifer = useSelector(selectConfigExpressRegistrationPlace);

  return (
    <Routes>
      {/* Creates routes for both str and ltr */}
      {[`/${cityIdentifer}-str-portal`, `/${cityIdentifer}-ltr-portal`].map(
        (route) => (
          <Route
            key={route}
            path={route}
            element={<Layout />}
          >
            {/* Public routes */}
            <Route
              index
              element={<Landing />}
            />

            <Route
              path="email-check"
              element={<EmailCheck />}
            />
            <Route
              path="create-account"
              element={<CreateAccount />}
            />

            <Route
              path="error"
              element={
                <ErrorPage
                  message=""
                  showBackButton={false}
                />
              }
            />

            {/* Protected routes */}
            <Route element={<RequireAuth />}>
              <Route
                path="dashboard"
                element={
                  <ErrorBoundaryWrapper>
                    <Dashboard />
                  </ErrorBoundaryWrapper>
                }
              />
              <Route
                path="single-permit/:applicationNumber"
                element={<SinglePermit />}
              />
              <Route
                path="all-permits"
                element={<AllPermits />}
              />
              <Route
                path="account-update"
                element={<UpdateAccount />}
              />
              <Route
                path="tax-select-properties"
                element={<TaxSelectProperties />}
              />
              <Route
                path="tax-report-revenue"
                element={<TaxReport />}
              />
              <Route
                path="tax-confirm-and-sign"
                element={<TaxConfirmAndSign />}
              />
              <Route
                path="tax-payment"
                element={<TaxPayment />}
              />
              <Route
                path="tax-success"
                element={<TaxSuccess />}
              />

              {/* TAX CERTIFICATE ROUTES */}
              <Route
                path="tax-cert-requirements"
                element={<TaxCertRequirements />}
              />
              <Route
                path="tax-cert-property"
                element={<TaxCertProperty />}
              />
              <Route
                path="tax-cert-registrant"
                element={<TaxCertRegistrant />}
              />
              <Route
                path="tax-cert-contacts"
                element={<TaxCertContacts />}
              />
              <Route
                path="tax-cert-confirmation"
                element={<TaxCertConfirmation />}
              />
              <Route
                path="tax-cert-success"
                element={<TaxCertSuccess />}
              />

              {/* APPLY ROUTES */}
              <Route
                path="apply-requirements"
                element={<ApplyRequirements />}
              />
              <Route
                path="apply-property"
                element={<ApplyProperty />}
              />
              <Route
                path="apply-registrant"
                element={<ApplyRegistrant />}
              />
              <Route
                path="apply-contacts"
                element={<ApplyContacts />}
              />
              <Route
                path="apply-property-details"
                element={<ApplyPropertyDetails />}
              />
              <Route
                path="apply-listings"
                element={<ApplyListings />}
              />
              <Route
                path="apply-documents"
                element={<ApplyDocuments />}
              />
              <Route
                path="apply-confirmation"
                element={<ApplyConfirmation />}
              />
              <Route
                path="apply-payment"
                element={<ApplyPayment />}
              />
              <Route
                path="apply-success"
                element={<ApplySuccess />}
              />

              {/* UPDATE ROUTES */}
              <Route
                path="update-registrant"
                element={<UpdateRegistrant />}
              />
              <Route
                path="update-contacts"
                element={<UpdateContacts />}
              />
              <Route
                path="update-property-details"
                element={<UpdatePropertyDetails />}
              />
              <Route
                path="update-documents"
                element={<UpdateDocuments />}
              />
              <Route
                path="update-confirmation"
                element={<UpdateConfirmation />}
              />
              <Route
                path="update-success"
                element={<UpdateSuccess />}
              />

              {/* RENEW ROUTES */}
              <Route
                path="renew-requirements"
                element={<RenewRequirements />}
              />
              <Route
                path="renew-registrant"
                element={<RenewRegistrant />}
              />
              <Route
                path="renew-contacts"
                element={<RenewContacts />}
              />
              <Route
                path="renew-property-details"
                element={<RenewPropertyDetails />}
              />
              <Route
                path="renew-documents"
                element={<RenewDocuments />}
              />
              <Route
                path="renew-confirmation"
                element={<RenewConfirmation />}
              />
              <Route
                path="renew-payment"
                element={<RenewPayment />}
              />
              <Route
                path="renew-success"
                element={<RenewSuccess />}
              />

              {/* BULK RENEW ROUTES */}
              <Route
                path="bulk-renew-select-properties"
                element={<BulkRenewSelectProperties />}
              />
              <Route
                path="bulk-renew-requirements"
                element={<BulkRenewRequirements />}
              />
              <Route
                path="bulk-renew-confirmation"
                element={<BulkRenewConfirmation />}
              />

              {/* COMMON FLOW Routes */}
              <Route
                path="payment-redirect"
                element={<PaymentRedirect />}
              />

              <Route
                path="payment-completed"
                element={<PaymentCompleted />}
              />
              <Route
                path="payment-cybersource"
                element={<CyberSource />}
              />
              <Route
                path="payment-cardconnect"
                element={<CardConnect />}
              />

              {/* Shown in a new tab */}
              <Route
                path="payment-success"
                element={<PaymentSuccess />}
              />
              <Route
                path="payment-pending"
                element={<PaymentSuccess />}
              />
              {/* Shown in a new tab */}
              <Route
                path="payment-error"
                element={<PaymentError />}
              />
              {/* Shown in a new tab — Euna cancel redirect */}
              <Route
                path="payment-cancel"
                element={<PaymentCancel />}
              />
            </Route>

            {/* ADMIN UPDATE ROUTES */}
            <Route element={<AdminRequireAuth />}>
              <Route
                path="admin-update-property"
                element={<AdminUpdateProperty />}
              />
              <Route
                path="admin-update-portal-account"
                element={<AdminUpdatePortalAccount />}
              />
              <Route
                path="admin-update-registrant"
                element={<AdminUpdateRegistrant />}
              />
              <Route
                path="admin-update-owner"
                element={<AdminUpdateOwner />}
              />
              <Route
                path="admin-update-contacts"
                element={<AdminUpdateContacts />}
              />
              <Route
                path="admin-update-property-details"
                element={<AdminUpdatePropertyDetails />}
              />
              <Route
                path="admin-update-documents"
                element={<AdminUpdateDocuments />}
              />
              <Route
                path="admin-update-confirmation"
                element={<AdminUpdateConfirmation />}
              />
            </Route>
            {/* 
                    Note: this route is not wrapped in AdminRequireAuth becuase we clear the adminUpdate redux store on arrival to this page.
                    If it was included in the above, the user is immediately kicked back to the login screen upon arrival to this page
                */}
            <Route
              path="admin-update-success"
              element={<AdminUpdateSuccess />}
            />
          </Route>
        )
      )}

      <Route
        path="*"
        element={<ErrorPage showBackButton={false} />}
      />
    </Routes>
  );
};

export default AppRoutes;
